import { useEffect, useState } from "react";
import {
  IconCash,
  IconChartBar,
  IconClockHour4,
  IconCpu,
  IconMessages,
} from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";
import { SectionCard } from "../../../../components/common/section-card";
import { StatCard } from "../../../../components/common/stat-card";
import { Pagination } from "../../../../components/common/pagination";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { SearchBar } from "../../../../components/common/input";
import {
  adminAiChatService,
  type AdminAiChatStats,
  type AdminAiChatConversation,
  type AdminAiChatQuery,
} from "../../../api_client";
import { AiExtractionWeeklyChart } from "./ai-extraction-weekly-chart";
import { ConversationDrawer } from "./conversation-drawer";
import { formatDateTime, formatNumber, formatUSD } from "../utils/format";
import { Td, Th } from "../utils/format-helpers";

const PAGE_SIZE = 25;

export function AiChatTab() {
  const [stats, setStats] = useState<AdminAiChatStats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);

  const [listData, setListData] = useState<{
    items: AdminAiChatConversation[];
    totalCount: number;
    pageCount: number;
  } | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    adminAiChatService
      .stats()
      .then((res) => {
        if (cancelled) return;
        setStats(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        setStatsError("Failed to load chat stats.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    const query: AdminAiChatQuery = {
      page,
      pageSize: PAGE_SIZE,
      sortBy: "createdAt",
      sortOrder: "desc",
    };
    if (debouncedSearch) query.search = debouncedSearch;
    adminAiChatService
      .conversations(query)
      .then((res) => {
        if (cancelled) return;
        setListData({
          items: res.data.data,
          totalCount: res.data.meta.totalCount,
          pageCount: res.data.meta.pageCount,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setListError("Failed to load conversations.");
      })
      .finally(() => {
        if (cancelled) return;
        setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch]);

  return (
    <>
      <AiChatStatsSection stats={stats} statsError={statsError} />

      <SectionCard shadow title="Conversations" className="gap-4 max-md:px-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[280px] flex-1 max-sm:min-w-0 max-sm:basis-full">
            <SearchBar
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by conversation or user…"
            />
          </div>
        </div>

        {listError ? (
          <Text variant="body" tone="body" className="!text-accentError">{listError}</Text>
        ) : listLoading && !listData ? (
          <ShimmerLoader message="Loading conversations…" />
        ) : !listData || listData.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12">
            <IconChartBar size={32} className="text-textMuted" />
            <Text variant="body" tone="muted">No conversations match these filters.</Text>
          </div>
        ) : (
          <ConversationsTable
            items={listData.items}
            onRowClick={(chatId) => setSelectedChatId(chatId)}
          />
        )}

        {listData && listData.pageCount > 1 ? (
          <div className="flex flex-col items-center gap-2">
            <Pagination
              page={page}
              pageCount={listData.pageCount}
              onPageChange={setPage}
            />
            <Text variant="bodySm" tone="muted">
              {formatNumber(listData.totalCount)} total · page {page} of {listData.pageCount}
            </Text>
          </div>
        ) : null}
      </SectionCard>

      {selectedChatId && (
        <ConversationDrawer
          chatId={selectedChatId}
          onClose={() => setSelectedChatId(null)}
        />
      )}
    </>
  );
}

function AiChatStatsSection({ stats, statsError }: { stats: AdminAiChatStats | null; statsError: string | null }) {
  if (statsError) {
    return <Text variant="body" tone="body" className="!text-accentError">{statsError}</Text>;
  }
  if (!stats) {
    return <ShimmerLoader message="Loading AI chat stats…" />;
  }
  return (
    <>
      <div className="grid grid-cols-4 gap-4 max-md:grid-cols-2 max-md:gap-3 max-sm:grid-cols-1">
        <StatCard
          icon={<IconCash size={22} />}
          label="Chat spend 7d"
          value={formatUSD(stats.last7Days.totalCostUSD)}
          sub={`${formatNumber(stats.last7Days.totalCalls)} messages`}
        />
        <StatCard
          icon={<IconCash size={22} />}
          label="Chat spend 30d"
          value={formatUSD(stats.last30Days.totalCostUSD)}
          sub={`${formatNumber(stats.last30Days.totalCalls)} messages`}
        />
        <StatCard
          icon={<IconCpu size={22} />}
          label="Tokens (last 30d)"
          value={formatNumber(stats.last30Days.totalTokens)}
          sub={`${formatUSD(stats.allTime.totalCostUSD)} spend all-time`}
          iconTone="light"
        />
        <StatCard
          icon={<IconClockHour4 size={22} />}
          label="Avg chat duration"
          value={`${stats.last30Days.avgDurationMs != null ? `${(stats.last30Days.avgDurationMs / 1000).toFixed(1)}s` : "—"}`}
          sub={`${formatNumber(stats.last30Days.totalCalls)} messages (30d)`}
          iconTone="light"
        />
      </div>

      <div className="grid grid-cols-5 gap-4 max-md:grid-cols-1 max-md:gap-3">
        <SectionCard shadow title="Cost by week (last 12 weeks)" className="col-span-3 gap-3 max-md:col-span-1 max-md:px-4">
          <AiExtractionWeeklyChart data={stats.byWeek} />
        </SectionCard>

        <SectionCard shadow title="Top users (30d)" className="col-span-2 gap-2 max-md:col-span-1 max-md:px-4">
          {stats.topUsers.length === 0 ? (
            <Text variant="bodySm" tone="muted">No chat activity in the last 30 days.</Text>
          ) : (
            <div className="flex max-h-[180px] flex-col divide-y divide-borderDefault overflow-y-auto">
              {stats.topUsers.map((u) => (
                <div key={u.userId ?? Math.random()} className="flex shrink-0 items-center justify-between gap-3 py-2">
                  <div className="min-w-0 flex flex-col">
                    <Text variant="bodySmBold" tone="dark" className="truncate">
                      {u.fullName ?? u.email ?? u.userId ?? "Unknown user"}
                    </Text>
                    {u.fullName && u.email ? (
                      <Text variant="bodyXs" tone="muted" className="truncate">{u.email}</Text>
                    ) : null}
                  </div>
                  <div className="shrink-0 text-right">
                    <Text variant="bodySmBold" tone="dark">{formatUSD(u.costUSD)}</Text>
                    <Text variant="bodyXs" tone="muted">{formatNumber(u.calls)} msgs</Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </>
  );
}

function ConversationsTable({
  items,
  onRowClick,
}: {
  items: AdminAiChatConversation[];
  onRowClick: (chatId: string) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead className="bg-bgLight">
          <tr>
            <Th>Last activity</Th>
            <Th>User</Th>
            <Th>Conversation</Th>
            <Th align="right">Messages</Th>
            <Th align="right">Tokens</Th>
            <Th align="right">Cost</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr
              key={row.chatId}
              className="cursor-pointer border-t border-borderDefault transition-colors hover:bg-bgLight"
              onClick={() => onRowClick(row.chatId)}
            >
              <Td>
                <Text variant="bodySm" tone="dark">{formatDateTime(row.lastMessageAt)}</Text>
              </Td>
              <Td>
                <Text variant="bodySm" tone="dark" className="truncate">
                  {row.user.fullName ?? row.user.email ?? "—"}
                </Text>
                {row.user.fullName && row.user.email ? (
                  <Text variant="bodyXs" tone="muted" className="truncate">{row.user.email}</Text>
                ) : null}
              </Td>
              <Td>
                <div className="flex items-start gap-2">
                  <IconMessages size={16} className="mt-0.5 shrink-0 text-textMuted" />
                  <Text variant="bodySm" tone="dark" className="line-clamp-2">
                    {row.title ?? row.lastMessagePreview ?? "—"}
                  </Text>
                </div>
              </Td>
              <Td align="right">
                <Text variant="bodySm" tone="dark">{formatNumber(row.messageCount)}</Text>
              </Td>
              <Td align="right">
                <Text variant="bodySm" tone="dark">{formatNumber(row.totalTokens)}</Text>
              </Td>
              <Td align="right">
                <Text variant="bodySm" tone="dark">{formatUSD(row.totalCostUSD)}</Text>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
